import { API_BASE_URL } from '../config';
import { StockItem, StockMovement, MaterialGroup, CustomStatus, ItemStatus, MovementType } from '../types';

// API response wrapper
interface ApiResponse<T> {
  items?: T[];
  totalCount?: number;
  page?: number;
  pageSize?: number;
}

// Request types
export interface CreateStockItemRequest {
  name: string;
  itemType: string;
  skuCode?: string;
  width?: number;
  height?: number;
  thickness?: number;
  weightKg?: number;
  quantity?: number;
  minStock?: number;
  unit?: string;
  location?: string;
  costPerUnit?: number;
  parentItemId?: string;
  relatedOrderId?: string;
  fiscalDocId?: string;
  batchNumber?: string;
  expirationDate?: string;
  purchaseDate?: string;
  barcodes?: string[];
  tags?: string[];
}

export interface RecordMovementRequest {
  itemId: string;
  movementType: MovementType;
  quantity: number;
  reason?: string;
  relatedOrderId?: string;
  fiscalDocId?: string;
}

// Helper for authenticated requests
async function authFetch(url: string, options: RequestInit = {}, accessToken: string): Promise<Response> {
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${accessToken}`,
    ...options.headers,
  };

  return fetch(url, { ...options, headers });
}

// Stock Items API
export async function fetchStockItems(
  accessToken: string,
  params?: {
    search?: string;
    status?: ItemStatus;
    itemType?: string;
    groupId?: string;
    belowMinStock?: boolean;
    page?: number;
    size?: number;
  }
): Promise<ApiResponse<StockItem>> {
  const queryParams = new URLSearchParams();
  if (params?.search) queryParams.set('search', params.search);
  if (params?.status) queryParams.set('status', params.status);
  if (params?.itemType) queryParams.set('itemType', params.itemType);
  if (params?.groupId) queryParams.set('groupId', params.groupId);
  if (params?.belowMinStock) queryParams.set('belowMinStock', 'true');
  if (params?.page !== undefined) queryParams.set('page', params.page.toString());
  if (params?.size) queryParams.set('size', params.size.toString());

  const query = queryParams.toString();
  const url = `${API_BASE_URL}/storage/items${query ? `?${query}` : ''}`;

  const response = await authFetch(url, {}, accessToken);
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Erro ao buscar itens');
  }

  const json = await response.json();
  // Handle wrapped response format {success: true, data: {...}}
  return json.data || json;
}

export async function fetchStockItem(accessToken: string, id: string): Promise<StockItem> {
  const response = await authFetch(`${API_BASE_URL}/storage/items/${id}`, {}, accessToken);
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Erro ao buscar item');
  }
  return response.json();
}

export async function fetchStockItemByBarcode(accessToken: string, barcode: string): Promise<StockItem> {
  const response = await authFetch(`${API_BASE_URL}/storage/items/barcode/${encodeURIComponent(barcode)}`, {}, accessToken);
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Erro ao buscar item por código de barras');
  }
  return response.json();
}

export async function createStockItem(accessToken: string, data: CreateStockItemRequest): Promise<StockItem> {
  const response = await authFetch(`${API_BASE_URL}/storage/items`, {
    method: 'POST',
    body: JSON.stringify(data),
  }, accessToken);

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Erro ao criar item');
  }

  return response.json();
}

export async function updateStockItem(accessToken: string, id: string, data: Partial<CreateStockItemRequest>): Promise<StockItem> {
  const response = await authFetch(`${API_BASE_URL}/storage/items/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }, accessToken);

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Erro ao atualizar item');
  }

  return response.json();
}

export async function updateStockItemStatus(
  accessToken: string,
  id: string,
  status?: ItemStatus,
  customStatusId?: number,
  reason?: string
): Promise<StockItem> {
  const queryParams = new URLSearchParams();
  if (status) queryParams.set('status', status);
  if (customStatusId) queryParams.set('customStatusId', customStatusId.toString());
  if (reason) queryParams.set('reason', reason);

  const response = await authFetch(
    `${API_BASE_URL}/storage/items/${id}/status?${queryParams.toString()}`,
    { method: 'PUT' },
    accessToken
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Erro ao atualizar status');
  }

  return response.json();
}

export async function deleteStockItem(accessToken: string, id: string, reason?: string): Promise<void> {
  const queryParams = reason ? `?reason=${encodeURIComponent(reason)}` : '';
  const response = await authFetch(
    `${API_BASE_URL}/storage/items/${id}${queryParams}`,
    { method: 'DELETE' },
    accessToken
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Erro ao excluir item');
  }
}

// Stock Movements API
export async function fetchStockMovements(
  accessToken: string,
  params?: {
    itemId?: string;
    movementType?: MovementType;
    startDate?: string;
    endDate?: string;
    page?: number;
    size?: number;
  }
): Promise<ApiResponse<StockMovement>> {
  const queryParams = new URLSearchParams();
  if (params?.itemId) queryParams.set('itemId', params.itemId);
  if (params?.movementType) queryParams.set('movementType', params.movementType);
  if (params?.startDate) queryParams.set('startDate', params.startDate);
  if (params?.endDate) queryParams.set('endDate', params.endDate);
  if (params?.page !== undefined) queryParams.set('page', params.page.toString());
  if (params?.size) queryParams.set('size', params.size.toString());

  const query = queryParams.toString();
  const url = `${API_BASE_URL}/storage/movements${query ? `?${query}` : ''}`;

  const response = await authFetch(url, {}, accessToken);
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Erro ao buscar movimentações');
  }

  const json = await response.json();
  // Handle wrapped response format {success: true, data: {...}}
  return json.data || json;
}

export async function recordMovement(accessToken: string, data: RecordMovementRequest): Promise<StockMovement> {
  const response = await authFetch(`${API_BASE_URL}/storage/movements`, {
    method: 'POST',
    body: JSON.stringify(data),
  }, accessToken);

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Erro ao registrar movimentação');
  }

  return response.json();
}

// Material Groups API
export async function fetchMaterialGroups(accessToken: string): Promise<MaterialGroup[]> {
  const response = await authFetch(`${API_BASE_URL}/storage/groups`, {}, accessToken);
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Erro ao buscar grupos');
  }
  const json = await response.json();
  // Handle both direct array and wrapped response formats
  return Array.isArray(json) ? json : (json.data || json.items || []);
}

export async function createMaterialGroup(accessToken: string, name: string): Promise<MaterialGroup> {
  const response = await authFetch(`${API_BASE_URL}/storage/groups`, {
    method: 'POST',
    body: JSON.stringify({ name }),
  }, accessToken);

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Erro ao criar grupo');
  }

  const json = await response.json();
  return json.data || json;
}

// Custom Statuses API
export async function fetchCustomStatuses(accessToken: string): Promise<CustomStatus[]> {
  const response = await authFetch(`${API_BASE_URL}/storage/config/statuses`, {}, accessToken);
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Erro ao buscar status customizados');
  }
  const json = await response.json();
  // Handle both direct array and wrapped response formats
  return Array.isArray(json) ? json : (json.data || json.items || []);
}

export async function createCustomStatus(
  accessToken: string,
  name: string,
  color?: string
): Promise<CustomStatus> {
  const response = await authFetch(`${API_BASE_URL}/storage/config/statuses`, {
    method: 'POST',
    body: JSON.stringify({ name, color }),
  }, accessToken);

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Erro ao criar status');
  }

  return response.json();
}

// Scrap creation
export async function createScrap(
  accessToken: string,
  parentItemId: string,
  width: number,
  height: number,
  quantity: number,
  relatedOrderId?: string
): Promise<StockItem> {
  const response = await authFetch(`${API_BASE_URL}/storage/items/scrap`, {
    method: 'POST',
    body: JSON.stringify({
      parentItemId,
      width,
      height,
      quantity,
      relatedOrderId,
    }),
  }, accessToken);

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Erro ao criar retalho');
  }

  return response.json();
}

// Fetch children (scraps generated from a parent item)
export async function fetchChildItems(accessToken: string, parentItemId: string): Promise<StockItem[]> {
  const response = await authFetch(
    `${API_BASE_URL}/storage/items?parentItemId=${encodeURIComponent(parentItemId)}`,
    {},
    accessToken
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Erro ao buscar retalhos');
  }

  const json = await response.json();
  const data = json.data || json;
  return data.items || [];
}
